import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

echarts.use([TooltipComponent, GridComponent, LegendComponent, LineChart, CanvasRenderer]);

interface PromptInjectionDriftChartProps {
  data: { eventIndex: number; pirsPercent: number }[];
  title: string;
  description: string;
}

const PromptInjectionDriftChart: React.FC<PromptInjectionDriftChartProps> = ({ data, title, description }) => {
  const { getThemeColor } = useAppContext();

  // Sort data by event index
  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => a.eventIndex - b.eventIndex);
  }, [data]);

  const eventIndices = sortedData.map(item => item.eventIndex);
  const pirsPercentages = sortedData.map(item => item.pirsPercent);

  // Calculate max value for y-axis (add some padding)
  const maxPIRS = Math.max(...pirsPercentages, 100);
  const yAxisMax = Math.ceil(maxPIRS / 10) * 10 + 10;

  // Ensure we have valid colors with fallbacks
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';
  const dangerColor = getThemeColor('danger') || '#dc3545';
  const warningColor = getThemeColor('warning') || '#ffc107';

  const options = {
    color: primaryColor,
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        return `<strong>Event ${param.name}:</strong> ${param.value.toFixed(2)}% PIRS`;
      }
    },
    xAxis: {
      type: 'category',
      name: 'Event Index',
      data: eventIndices,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor }
    },
    yAxis: {
      type: 'value',
      name: 'PIRS (%)',
      min: 0,
      max: yAxisMax,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor },
      splitLine: {
        show: true,
        lineStyle: {
          color: borderColor,
          type: 'dashed'
        }
      }
    },
    series: [
      {
        type: 'line',
        name: 'PIRS',
        data: pirsPercentages,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          color: primaryColor,
          width: 2
        },
        itemStyle: {
          color: primaryColor
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: primaryColor + '40'
              },
              {
                offset: 1,
                color: primaryColor + '10'
              }
            ]
          }
        },
        markLine: {
          data: [
            {
              yAxis: 70,
              name: 'High Risk Threshold',
              lineStyle: {
                color: dangerColor,
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'High Risk (70%)',
                position: 'end'
              }
            },
            {
              yAxis: 35,
              name: 'Medium Risk Threshold',
              lineStyle: {
                color: warningColor,
                type: 'dashed',
                width: 2
              },
              label: {
                formatter: 'Medium Risk (35%)',
                position: 'end'
              }
            }
          ]
        }
      }
    ],
    grid: { left: '15%', right: '10%', bottom: '15%', top: '15%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">{title}</h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '400px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default PromptInjectionDriftChart;
